import type { ComponentProps } from "react";
import { useQuery } from "@tanstack/react-query";

import { consultationsQueriesStore } from "@/features/consultations/api";
import { APPOINTMENT_TYPE } from "@/features/consultations/models";
import type { InjectionFormOption } from "@/features/consultations/screens";
import { getDisabledConsultationOptionMessage } from "@/features/consultations/utils";
import { useCurrentMedicationRequests } from "@/features/my-meds/hooks";
import { CATEGORY_SLUG } from "@/shared/domains";
import { Popover, RadioGroup } from "@/shared/ui";

interface RadioGroupCardsProps extends ComponentProps<typeof RadioGroup.Root> {
  options: readonly InjectionFormOption[];
}

export const RadioGroupCards = ({
  options,
  id,
  ...props
}: RadioGroupCardsProps) => {
  const { data: upcomingMedicationRequestConsultations } = useQuery(
    consultationsQueriesStore.consultations.appointments._ctx.list._ctx.upcoming
      ._ctx.medicationRequest,
  );

  const { data: currentMedicationRequests } = useCurrentMedicationRequests();

  const hasUpcomingFollowUpConsultation = Boolean(
    upcomingMedicationRequestConsultations?.some(
      (consultation) =>
        consultation.type === APPOINTMENT_TYPE.MEDICATION_REQUEST_INITIAL ||
        consultation.type === APPOINTMENT_TYPE.MEDICATION_REQUEST_FOLLOW_UP,
    ),
  );

  const hasPendingMedicationRequest = Boolean(
    currentMedicationRequests?.some(
      (request) =>
        request.product.category.slug === CATEGORY_SLUG.WEIGHT_LOSS ||
        request.product.category.slug === CATEGORY_SLUG.MICRODOSING,
    ),
  );

  const hasUpcomingNurseConsultation =
    upcomingMedicationRequestConsultations?.some(
      (consultation) => consultation.type === APPOINTMENT_TYPE.OTHER,
    );

  return (
    <RadioGroup.Root
      id={id}
      className="flex flex-col items-stretch gap-6 md:flex-row"
      isCard
      {...props}
    >
      {options.map((option, idx) => {
        const disabled =
          option.appointmentType === APPOINTMENT_TYPE.OTHER
            ? hasUpcomingNurseConsultation
            : hasUpcomingFollowUpConsultation || hasPendingMedicationRequest;

        const disabledReason = getDisabledConsultationOptionMessage(
          hasUpcomingFollowUpConsultation,
          option.appointmentType,
        );

        return (
          <Popover.Root key={option.value}>
            <Popover.Trigger asChild>
              <div className="w-full">
                <RadioGroup.Item
                  disabled={disabled}
                  value={option.value}
                  id={`${id}-${idx}-${option.value}`}
                  className="h-full"
                >
                  <RadioGroup.Label>{option.label}</RadioGroup.Label>
                </RadioGroup.Item>
              </div>
            </Popover.Trigger>

            {disabled && (
              <Popover.Content className="text-center">
                {disabledReason}
              </Popover.Content>
            )}
          </Popover.Root>
        );
      })}
    </RadioGroup.Root>
  );
};
